/**
 * Учётная запись, способы входа, сессии, организация.
 *
 * Три правила спеки §5, из-за которых эти вызовы устроены именно так:
 *
 * * **Почта здесь не меняется.** Смена адреса — отдельный поток: письмо уходит
 *   на новый адрес, уведомление о попытке — на старый, событие пишется в
 *   историю на дозапись. Для B2B-аккаунта смена почты — материал для разбора
 *   инцидента, а не поле в форме.
 * * **Смена пароля отзывает все сессии, кроме текущей.** Это не побочный
 *   эффект, а смысл действия: пароль меняют, когда думают, что его знает кто-то
 *   ещё.
 * * **Последний способ входа снять нельзя.** Пароль и провайдер независимы;
 *   отвязать Яндекс ID без пароля — это потерять аккаунт, и сервер отвечает
 *   `409 last_login_method`.
 */

import { api } from './client';
import type { components } from './schema';

export type Account = components['schemas']['Account'];
export type AccountUpdate = components['schemas']['AccountUpdate'];
export type LoginMethods = components['schemas']['LoginMethods'];
export type UserSession = components['schemas']['UserSession'];
export type Organization = components['schemas']['Organization'];
export type OrganizationUpdate = components['schemas']['OrganizationUpdate'];

export function getAccount(signal?: AbortSignal): Promise<Account> {
  return api.get<Account>('/settings/account', { signal });
}

export function updateAccount(body: AccountUpdate): Promise<Account> {
  return api.patch<Account>('/settings/account', body);
}

/** Запрос смены почты. Ответ `202`: адрес сменится после подтверждения по ссылке. */
export function requestEmailChange(email: string, stepUpToken?: string): Promise<void> {
  return api.post<void>('/settings/account/email', { email }, { stepUpToken });
}

export function changePassword(current: string, next: string): Promise<void> {
  return api.put<void>('/auth/password', {
    current_password: current,
    new_password: next,
  });
}

export function listLoginMethods(signal?: AbortSignal): Promise<LoginMethods> {
  return api.get<LoginMethods>('/auth/identities', { signal });
}

export function listSessions(signal?: AbortSignal): Promise<UserSession[]> {
  return api.get<UserSession[]>('/auth/sessions', { signal });
}

export function revokeSession(id: string): Promise<void> {
  return api.delete<void>(`/auth/sessions/${encodeURIComponent(id)}`);
}

/**
 * Выйти на всех устройствах. Отзывает и текущую сессию тоже — иначе действие
 * называлось бы иначе. Экран обязан после этого показать форму входа.
 */
export function logoutAll(): Promise<void> {
  return api.post<void>('/auth/logout-all');
}

export function getOrganization(signal?: AbortSignal): Promise<Organization> {
  return api.get<Organization>('/organizations/current', { signal });
}

export function updateOrganization(body: OrganizationUpdate): Promise<Organization> {
  return api.patch<Organization>('/organizations/current', body);
}
